// frontend/src/pages/freelancer/FreelancerNotifications.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FreelancerSidebarLayout from './FreelancerSidebarLayout';
import axios from 'axios';
import './freelancerStyles.css';

function FreelancerNotifications() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [errorMsg, setErrorMsg] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) { navigate('/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) { setErrorMsg('Configuration error.'); return null; }
    return baseURL;
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;
        const res = await axios.get(`${baseURL}/api/staff/notifications`, { headers });
        setNotifications(res.data.notifications || []);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem('ogestyle_token'); navigate('/login');
        } else { setErrorMsg('Failed to load notifications'); }
      } finally { setLoading(false); }
    };
    fetchNotifications();
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  const markAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      await axios.patch(`${baseURL}/api/staff/notifications/${notificationId}/read`, {}, { headers });
      setNotifications(notifications.map(n => n._id === notificationId ? { ...n, read: true } : n));
    } catch (err) {
    }
  };

  const markAllAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      await axios.patch(`${baseURL}/api/staff/notifications/mark-all-read`, {}, { headers });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (err) {
      setErrorMsg('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'booking': return '📅';
      case 'payment': return '💳';
      case 'message': return '💬';
      case 'review': return '⭐';
      case 'reminder': return '⏰';
      default: return '🔔';
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  if (loading) return <FreelancerSidebarLayout><div style={{ padding: '40px', textAlign: 'center' }}><p>Loading notifications...</p></div></FreelancerSidebarLayout>;

  return (
    <FreelancerSidebarLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: isMobile ? '15px' : '20px' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '30px', gap: '15px' }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '24px' : '28px' }}>Notifications</h2>
            <p style={{ margin: 0, color: '#666', fontSize: isMobile ? '14px' : '16px' }}>{notifications.filter(n => !n.read).length} unread</p>
          </div>
          {notifications.filter(n => !n.read).length > 0 && (
            <button onClick={markAllAsRead} style={{ padding: isMobile ? '8px 16px' : '10px 20px', border: '1px solid #001d4a', borderRadius: '8px', background: '#fff', color: '#001d4a', fontSize: isMobile ? '13px' : '14px', fontWeight: '500', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>Mark All as Read</button>
          )}
        </div>

        {errorMsg && <div style={{ background: '#fff3f3', border: '1px solid #c00', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}><p style={{ margin: 0, color: '#c00' }}>{errorMsg}</p></div>}

        <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', marginBottom: '25px', flexWrap: 'wrap' }}>
          {['all', 'unread', 'read'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: isMobile ? '8px 16px' : '10px 20px', border: filter === f ? '2px solid #001d4a' : '1px solid #ddd', borderRadius: '8px', background: filter === f ? '#001d4a' : '#fff', color: filter === f ? '#fff' : '#666', fontSize: isMobile ? '13px' : '14px', fontWeight: filter === f ? '600' : '500', cursor: 'pointer', textTransform: 'capitalize' }}>{f} ({notifications.filter(n => { if (f === 'all') return true; if (f === 'unread') return !n.read; if (f === 'read') return n.read; return true; }).length})</button>
          ))}
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="dashboard-card" style={{ padding: isMobile ? '30px 20px' : '40px', textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '40px' : '48px', marginBottom: '15px' }}>🔔</div>
            <h3 style={{ color: '#666', marginBottom: '10px', fontSize: isMobile ? '18px' : '20px' }}>No Notifications</h3>
            <p style={{ color: '#999', fontSize: isMobile ? '14px' : '15px' }}>{filter === 'all' ? 'You\'re all caught up!' : `No ${filter} notifications.`}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {filteredNotifications.map((notification) => (
              <div key={notification._id} className="dashboard-card" style={{ padding: isMobile ? '15px' : '20px', cursor: notification.actionUrl ? 'pointer' : 'default', background: notification.read ? '#fff' : '#f8f9ff', border: notification.read ? '1px solid #e0e0e0' : '2px solid #001d4a20', transition: 'all 0.2s' }} onClick={() => { if (!notification.read) markAsRead(notification._id); if (notification.actionUrl) navigate(notification.actionUrl); }} onMouseEnter={(e) => { if (notification.actionUrl) { e.currentTarget.style.transform = 'translateX(4px)'; } }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; }}>
                <div style={{ display: 'flex', gap: isMobile ? '12px' : '15px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: isMobile ? '24px' : '28px', flexShrink: 0 }}>{getNotificationIcon(notification.type)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '10px' }}>
                      <h4 style={{ margin: 0, fontSize: isMobile ? '15px' : '16px', fontWeight: '600', color: '#001d4a' }}>{notification.title}</h4>
                      {!notification.read && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#001d4a', flexShrink: 0, marginTop: '5px' }}></span>}
                    </div>
                    <p style={{ margin: '0 0 8px 0', fontSize: isMobile ? '13px' : '14px', color: '#666', lineHeight: '1.5' }}>{notification.message}</p>
                    <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#999' }}>{new Date(notification.createdAt).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerNotifications;
